import React from 'react';
import FormModal from './Generic/FormModal';
import CliftonStrengthForm from './CliftonStrengthForm';
import { PlusCircle as AddIcon, FloppyFill } from './Generic/Icons';
import useABC from '../Provider/TableContext/ABCContext';

const DefaultFormData = { cliftonstrength: "", achievements: []};

export default function CliftonStrengthAssociationModal({title, actionName, newActionName,  onClose: _onClose,...props}) {
    const [formData, _setFormData] = React.useState(DefaultFormData);
    const { abcs, loading, updateABC } = useABC();

    const onClose = React.useCallback(() => {
        _setFormData(DefaultFormData)
        _onClose?.(); 
    }, [_onClose]);

    const abcMap= React.useMemo(() => {
        const result = {};
        abcs.forEach(abc => result[abc.mcb_abcid] = abc);
        return result;
    }, [abcs]);

    const strengthsAchievements = React.useMemo(() => {
        const result = {};
        if (Array.isArray(abcs)) {
            abcs.forEach(({mcb_cliftonstrengths, mcb_abcid} = {}) => {
                let strengths;
                if (mcb_cliftonstrengths && typeof mcb_cliftonstrengths === "string") {
                    strengths = mcb_cliftonstrengths.split(",");
                } else if (isNaN(mcb_cliftonstrengths)){
                    strengths = [];
                } else {
                    strengths = [`${mcb_cliftonstrengths}`]
                }
                if (mcb_abcid) {
                    strengths.forEach((strength) => {
                        if (Array.isArray(result[strength])) {
                            result[strength].push(mcb_abcid)
                        } else {
                            result[strength] = [mcb_abcid];
                        }
                    })
                }
            })
        }
        return result;
    }, [abcs]);

    const strengthAchievements = React.useMemo(() => (
        (strengthsAchievements && strengthsAchievements[formData?.cliftonstrength]) || []
    ), [strengthsAchievements, formData.cliftonstrength]);

    const hasChanged = React.useMemo(() => (
        formData.cliftonstrength && !(
            strengthAchievements?.length ===  formData.achievements?.length
            && strengthAchievements.every(oA => formData.achievements.includes(oA))
        )
    ), [formData, strengthAchievements])

    const onSubmit = React.useCallback(async () => {
        if (!formData?.achievements?.length || !formData?.cliftonstrength) return false;
        if (!hasChanged) return true;

        const cliftonstrength = formData.cliftonstrength;
        const oldAchievements = Array.from(strengthAchievements);
        const promises = [];

        formData.achievements.forEach(associated => {
            const oldIndex = oldAchievements.findIndex(oA => oA === associated);

            if (oldIndex < 0) {
                // associating new relations which are not present
                const abcs = abcMap[associated];
                const cliftonstrengths = abcMap[associated].mcb_cliftonstrengths?.length?
                    abcMap[associated].mcb_cliftonstrengths.split(",") : [];
                if (!cliftonstrengths.includes(cliftonstrength)) {
                    cliftonstrengths.push(cliftonstrength);
                    promises.push(updateABC({
                        ...abcs,
                        mcb_cliftonstrengths: cliftonstrengths.join(",")
                    }));
                }
                return false;
            } else {
                // removing all the common achievements from the oldAchievements
                oldAchievements.splice(oldIndex, 1);
                return true;
            }
        });

        // after removing all common ones oldAchievements array should only have a 
        oldAchievements.forEach(disassociated => {
            const abcs = abcMap[disassociated];
            const cliftonstrengths = abcMap[disassociated].mcb_cliftonstrengths?.length?
                abcMap[disassociated].mcb_cliftonstrengths.split(",") : [];
            const disIndex = cliftonstrengths.findIndex(a => a === `${cliftonstrength}`);
            if (disIndex >= 0) {
                cliftonstrengths.splice(disIndex, 1);
                promises.push(updateABC({
                    ...abcs,
                    mcb_cliftonstrengths: cliftonstrengths.join(",")
                }));
            }
        })

        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
        return true;
    }, [formData, strengthAchievements, abcMap, updateABC, hasChanged]);
      
    const setFormData = React.useCallback((arg) => {
        _setFormData((oldData) => {
            let newData = typeof arg === "function"? arg(oldData) : arg;
            if (oldData.cliftonstrength !== newData.cliftonstrength) {
                newData.achievements = strengthsAchievements[newData.cliftonstrength] || [];
            }
            return newData;
        })
    }, [strengthsAchievements]);

    return (
        <>
            <FormModal
                title={(title || "Associate CliftonStrengths® to Achievements")}
                triggerName={actionName || <><AddIcon className="me-2"/> Associate CliftonStrengths®</>}
                actionName={(actionName || <><FloppyFill className="me-2"/> Associate CliftonStrengths®</>)}
                classNames={{
                    modalTitleClassName: "btn-primary",
                    modalDialogClassName: "modal-dialog-centered", 
                    modalTriggerClassName: "btn-outline-dark border-0 fs-5 p-0 shadow-none text-decoration-underline",
                    closeActionClassName: "btn icon-button"
                }}
                BodyComponent={CliftonStrengthForm}
                onClose={onClose}
                formData={formData}
                setFormData={setFormData}
                onSubmit={onSubmit}
                loading={loading}
                quitConfirmation={hasChanged}
                disableSubmit={!formData?.achievements?.length || !formData?.cliftonstrength}
                {...props}
            />
        </>
    );
}